/**
 * Editions — admin matice edic (operator only).
 *
 * Zobrazuje matici edice × moduly: pro každou edici které moduly se načtou
 * (dle category + allow_extra − deny). Zdroj: editions_matrix tool.
 * Viz wiki editions-and-gitlab-dev-platform.
 */
import { Fragment } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import { Layers, Check, Minus, RefreshCw } from 'lucide-react';

interface ModuleInfo {
  name: string;
  category: string;
  core: boolean;
  kind: string;
}
interface EditionInfo {
  name: string;
  categories: string[];
  allow_extra: string[];
  deny: string[];
  scope_tiers: Record<string, unknown>;
}
interface Cell {
  available: boolean;
  via: string;
}
interface MatrixResp {
  ok: boolean;
  modules: ModuleInfo[];
  editions: EditionInfo[];
  matrix: Record<string, Record<string, Cell>>;
}

const VIA_COLOR: Record<string, string> = {
  category: 'text-emerald-400',
  allow_extra: 'text-sky-400',
  deny: 'text-rose-400',
  reference: 'text-zinc-600',
  'mimo edici': 'text-zinc-600',
};

export default function EditionsPage() {
  const { t } = useTranslation();
  const q = useQuery<MatrixResp>({
    queryKey: ['editions-matrix'],
    queryFn: () => api.mcp<MatrixResp>('editions.matrix', {}),
    staleTime: 30_000,
  });

  const data = q.data;
  const editions = data?.editions ?? [];
  const modules = data?.modules ?? [];

  // moduly seskupené dle kategorie (pořadí kategorií)
  const CATEGORY_ORDER = ['runtime', 'operator', 'app', 'integration', 'dev-tooling', 'reference', '(nezařazeno)'];
  const byCategory = CATEGORY_ORDER
    .map((cat) => ({ cat, mods: modules.filter((m) => m.category === cat) }))
    .filter((g) => g.mods.length > 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('editions.title')}
        description={t('editions.description')}
        icon={<Layers size={20} />}
      />

      {/* Přehled edic */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {editions.map((e) => (
          <Card key={e.name}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Badge>{e.name}</Badge>
                <span className="text-xs text-zinc-500">{Object.keys(e.scope_tiers ?? {}).join(', ')}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-1">
              <div><span className="text-zinc-500">{t('editions.categoriesLabel')}</span> {e.categories.join(', ') || '—'}</div>
              {e.allow_extra?.length > 0 && (
                <div><span className="text-sky-400">+allow:</span> {e.allow_extra.join(', ')}</div>
              )}
              {e.deny?.length > 0 && (
                <div><span className="text-rose-400">−deny:</span> {e.deny.join(', ')}</div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Matice */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t('editions.matrixTitle')}</span>
            <button onClick={() => q.refetch()} className="text-zinc-400 hover:text-zinc-200" title={t('common.refresh')}>
              <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
            </button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {q.isLoading
            ? <div className="text-sm text-zinc-500">{t('common.loading')}</div>
            : !data?.ok || modules.length === 0
            ? <EmptyState title={t('editions.noData')} description={t('editions.noDataDesc')} />
            : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left py-2 pr-3 sticky left-0 bg-zinc-950">{t('editions.moduleColumn')}</th>
                      {editions.map((e) => (
                        <th key={e.name} className="px-2 py-2 text-center font-medium">{e.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {byCategory.map((g) => (
                      <Fragment key={g.cat}>
                        <tr className="bg-zinc-900/50">
                          <td colSpan={editions.length + 1} className="py-1 px-2 text-zinc-400 font-medium uppercase tracking-wide text-[10px]">
                            {g.cat === '(nezařazeno)'
                              ? t('editions.uncategorized', '(nezařazeno)')
                              : g.cat}
                          </td>
                        </tr>
                        {g.mods.map((m) => (
                          <tr key={m.name} className="border-b border-zinc-900 hover:bg-zinc-900/30">
                            <td className="py-1.5 pr-3 sticky left-0 bg-zinc-950 whitespace-nowrap">
                              {m.name}{m.core && <span className="text-amber-500 ml-1" title="core">★</span>}
                            </td>
                            {editions.map((e) => {
                              const cell = data.matrix[e.name]?.[m.name];
                              return (
                                <td key={e.name} className="px-2 py-1.5 text-center" title={cell?.via}>
                                  {cell?.available
                                    ? <Check size={13} className={`inline ${VIA_COLOR[cell.via] ?? 'text-emerald-400'}`} />
                                    : <Minus size={13} className="inline text-zinc-700" />}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
